/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { Cell, Column, Row, TableView, TableBody, TableHeader } from '@react-spectrum/table';
import ViewDetail from '@spectrum-icons/workflow/ViewDetail';
import { ActionButton, Tooltip, TooltipTrigger } from '@adobe/react-spectrum';

const messages = defineMessages({
    labelReferenceTable: {
        id: 'label.referenceTable',
        defaultMessage: 'Displays details of selected items'
    },
    actionView: {
        id: 'action.view',
        defaultMessage: 'View'
    },
    columnName: {
        id: 'column.name',
        defaultMessage: 'Name'
    },
    columnActions: {
        id: 'column.actions',
        defaultMessage: 'Actions'
    },
    columnId: {
        id: 'column.id',
        defaultMessage: 'ID'
    },
    columnUid: {
        id: 'column.uid',
        defaultMessage: 'UID'
    },
    columnSku: {
        id: 'column.sku',
        defaultMessage: 'SKU'
    },
    columnSlug: {
        id: 'column.slug',
        defaultMessage: 'Slug'
    },
    columnCombinedSku: {
        id: 'column.combinedSku',
        defaultMessage: 'Combined SKU'
    },
    columnCombinedSlug: {
        id: 'column.combinedSlug',
        defaultMessage: 'Combined Slug'
    },
    columnUrlPath: {
        id: 'column.urlPath',
        defaultMessage: 'URL Path'
    }
});

const ReferenceTable = props => {
    const { keyField, keyFieldKey, items, onView } = props;

    const { formatMessage } = useIntl();

    // Get translation for keyField
    const capitalizedKeyField = keyField[0].toUpperCase() + keyField.slice(1);
    const keyFieldLabel = messages[`column${capitalizedKeyField}`]
        ? formatMessage(messages[`column${capitalizedKeyField}`])
        : capitalizedKeyField;

    return (
        <TableView overflowMode="wrap" aria-label={formatMessage(messages.labelReferenceTable)}>
            <TableHeader>
                <Column key={keyFieldKey} width="20%">
                    {keyFieldLabel}
                </Column>
                <Column key="label">{formatMessage(messages.columnName)}</Column>
                <Column key="details" width="20%" align="end">
                    {formatMessage(messages.columnActions)}
                </Column>
            </TableHeader>
            <TableBody items={items}>
                {item => (
                    <Row>
                        {columnKey => (
                            <Cell>
                                {columnKey === 'details' && !item.unknown && onView ? (
                                    <TooltipTrigger placement="start">
                                        <ActionButton
                                            data-test-id="button-view-selection"
                                            isQuiet
                                            onPress={() => onView(item)}
                                            aria-label={formatMessage(messages.actionView)}
                                        >
                                            <ViewDetail />
                                        </ActionButton>
                                        <Tooltip>{formatMessage(messages.actionView)}</Tooltip>
                                    </TooltipTrigger>
                                ) : (
                                    item[columnKey]
                                )}
                            </Cell>
                        )}
                    </Row>
                )}
            </TableBody>
        </TableView>
    );
};

ReferenceTable.propTypes = {
    keyField: PropTypes.string.isRequired,
    keyFieldKey: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            unknown: PropTypes.bool
        })
    ).isRequired,
    onView: PropTypes.func
};

ReferenceTable.defaultProps = {
    keyFieldKey: 'key'
};

export default ReferenceTable;
